import { PageContainer } from '@ant-design/pro-components';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Spin } from 'antd';
import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import AgentCreationWorkspace from '../components/AgentCreationWorkspace';
import LegacyAgentsChat from '../components/LegacyAgentsChat';
import { isBusinessId, shouldUseHarnessCreation } from '../utils/harness-creation';
import {
  clearCreationQuery,
  getCreationQueryValue,
  getPendingCreationEntry,
  resolveCreationRecord,
} from '../utils/creation-record';
import { queryAgentChatThread } from '../services/chat';

export default function AgentsChat() {
  const [query, setQuery] = useQueryParams();
  const { organizationId } = useOrganizationPath();
  const { session } = useAppContext();
  const threadId = getCreationQueryValue(query.threadId);
  const record = useQuery({
    queryKey: ['agents-chat', 'record', organizationId, session.user.id, threadId],
    queryFn: () => queryAgentChatThread({ organizationId, threadId }),
    enabled: Boolean(organizationId && isBusinessId(threadId)),
    retry: false,
  });
  const resolution = useMemo(() => {
    try {
      if (threadId && !isBusinessId(threadId)) throw new Error('创作记录无效');
      if (threadId && record.data) return { query: resolveCreationRecord(query, record.data) };
      if (!threadId) return { entry: getPendingCreationEntry(query) };
      return {};
    } catch (error) {
      return { error };
    }
  }, [query, record.data, threadId]);
  useEffect(() => {
    if (resolution.query && !isEqual(query, resolution.query)) {
      setQuery(resolution.query, { reset: true, replace: true });
    }
  }, [query, resolution.query, setQuery]);

  const error = resolution.error || (threadId && record.error);
  const loading = threadId && record.isPending;
  const canonical = resolution.query || query;
  const harness = shouldUseHarnessCreation(query, threadId ? record.data : undefined);

  return (
    <PageContainer pageHeaderRender={false}>
      {error && (
        <Alert
          type="error"
          showIcon
          title="创作记录无法打开"
          description={error.message}
          action={
            <Button
              aria-label="返回"
              icon={<ArrowLeftOutlined />}
              onClick={() => setQuery(clearCreationQuery(query), { reset: true })}
            >
              返回
            </Button>
          }
        />
      )}
      {!error && loading && <Spin />}
      {!error && !loading && harness && (
        <AgentCreationWorkspace
          key={`${organizationId}:${session.user.id}`}
          creationQuery={canonical}
          entry={resolution.entry}
        />
      )}
      {!error && !loading && !harness && (
        <LegacyAgentsChat key={`${organizationId}:${session.user.id}`} />
      )}
    </PageContainer>
  );
}
